import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScreenInit } from '../useScreenInit.js';
import { CheckCircle2, ChevronRight, ChevronLeft, FileText, Users, Layers, ListChecks, Settings, Mail, Eye, Rocket, Search, AlertTriangle } from 'lucide-react';
import { getRole } from '../data/framework';
import { PICKS_PER_TYPE, domainProgress, questionnaireSize, sampleSelection } from '../utils/framework';
import type { CompetencyDomain, FrameworkItem } from '../types/framework';
import { RoleStep } from '../components/assessment/RoleStep';
import { DomainsStep } from '../components/assessment/DomainsStep';
import { QuestionPicker } from '../components/assessment/QuestionPicker';
import { QuestionnairePreview } from '../components/assessment/QuestionnairePreview';
const STEPS = [{
  id: 1,
  title: 'Assessment Details',
  icon: FileText
}, {
  id: 2,
  title: 'Type & Role',
  icon: Users
}, {
  id: 3,
  title: 'Competency Domains',
  icon: Layers
}, {
  id: 4,
  title: 'Select Questions',
  icon: ListChecks
}, {
  id: 5,
  title: 'Rating Scale',
  icon: Settings
}, {
  id: 6,
  title: 'Invite Respondents',
  icon: Mail
}, {
  id: 7,
  title: 'Review',
  icon: Eye
}, {
  id: 8,
  title: 'Launch',
  icon: Rocket
}];
const LAST_STEP = STEPS.length;
const REVIEW_STEP = 7;
export function CreateAssessment() {
  const initial = useScreenInit();
  const initialRoleId = typeof initial.role === 'string' && getRole(initial.role) ? initial.role : null;
  const initialRole = initialRoleId ? getRole(initialRoleId) : undefined;
  const [currentStep, setCurrentStep] = useState<number>(typeof initial.step === 'number' ? initial.step : 1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    dueDate: '',
    type: '360',
    scale: '5'
  });
  const [roleId, setRoleId] = useState<string | null>(initialRoleId);
  const [selected, setSelected] = useState<number[]>(initial.sampleSelection === true && initialRole ? sampleSelection(initialRole) : []);
  const [pendingRoleId, setPendingRoleId] = useState<string | null>(null);
  const role = roleId ? getRole(roleId) : undefined;
  const total = role ? questionnaireSize(role) : 0;
  const incompleteDomains = role ? role.domains.filter(d => !domainProgress(d, selected).complete) : [];
  const selectionComplete = !!role && incompleteDomains.length === 0;
  const canContinue = (currentStep !== 2 || !!role) && (currentStep !== 3 || !!role) && (currentStep !== 4 || selectionComplete) && (currentStep !== REVIEW_STEP || selectionComplete);
  const handleRoleSelect = (id: string) => {
    if (id === roleId) return;
    if (selected.length > 0) {
      setPendingRoleId(id);
      return;
    }
    setRoleId(id);
  };
  const confirmRoleChange = () => {
    if (!pendingRoleId) return;
    setRoleId(pendingRoleId);
    setSelected([]);
    setPendingRoleId(null);
  };
  const toggleItem = (item: FrameworkItem, domain: CompetencyDomain) => {
    setSelected(prev => {
      if (prev.includes(item.no)) return prev.filter(n => n !== item.no);
      const sameType = domain[item.type].filter(i => prev.includes(i.no));
      if (sameType.length >= PICKS_PER_TYPE) return prev;
      return [...prev, item.no];
    });
  };
  const handleNext = () => {
    if (canContinue && currentStep < LAST_STEP) setCurrentStep(currentStep + 1);
  };
  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };
  const renderNoRole = () => <div className="border border-dashed border-slate-300 rounded-xl py-16 text-center" data-id="element-945" data-mp="37p71kqsxn9nk">
      <Layers className="h-6 w-6 text-slate-400 mx-auto mb-3" data-id="element-946" data-mp="3cmz4efv48g6j" />
      <p className="text-sm font-medium text-slate-900" data-id="element-947" data-mp="3hmbs12tt9ra6">No role selected</p>
      <p className="text-sm text-slate-500 mt-1 mb-4" data-id="element-948" data-mp="3hnmsotrymix2">
        Choose the role being assessed to load its competency domains.
      </p>
      <button type="button" onClick={() => setCurrentStep(2)} className="text-sm font-medium text-slate-900 border border-slate-200 px-3 py-1.5 rounded-md hover:bg-slate-50" data-id="element-949" data-mp="wvujxlmzgjud">
        Choose a role
      </button>
    </div>;
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-950" data-mp="14gwzyu63r2rf">
            <div data-id="element-951" data-mp="zfhdcxoa0o6x">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-952" data-mp="zexxdmi6s9vj">
                Assessment Details
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-953" data-mp="1de7eqpi4pa13">
                Provide the basic information for this assessment cycle.
              </p>
            </div>
            <div className="space-y-4" data-id="element-954" data-mp="1n4s920qrrse7">
              <div data-id="element-955" data-mp="1n7xwlbjr2m7t">
                <label htmlFor="assessment-name" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-956" data-mp="1i7a4emjsbzc0">
                  Assessment Name *
                </label>
                <input id="assessment-name" type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} placeholder="e.g., 2026 Board Effectiveness Review" className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-957" data-mp="1s6977ohvd0r3" />
              </div>
              <div data-id="element-958" data-mp="1s0w9yf5cl11o">
                <label htmlFor="assessment-description" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-959" data-mp="1wtph19oaglgx">
                  Description
                </label>
                <textarea id="assessment-description" rows={3} value={formData.description} onChange={e => setFormData({
                ...formData,
                description: e.target.value
              })} placeholder="Brief description of the assessment goals..." className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-960" data-mp="1o0m4r4pdqenv" />
              </div>
              <div data-id="element-961" data-mp="g9q9rg6y4bzo">
                <label htmlFor="assessment-due" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-962" data-mp="ga8b2r8gtgeg">
                  Due Date *
                </label>
                <input id="assessment-due" type="date" value={formData.dueDate} onChange={e => setFormData({
                ...formData,
                dueDate: e.target.value
              })} className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-963" data-mp="18zfartjnoc1a" />
              </div>
            </div>
          </div>;
      case 2:
        return <RoleStep type={formData.type} roleId={roleId} onTypeChange={type => setFormData({
          ...formData,
          type
        })} onRoleSelect={handleRoleSelect} data-id="element-964" data-mp="1z6jq4khj8k8w" />;
      case 3:
        return role ? <DomainsStep role={role} data-id="element-965" data-mp="2dxke5xurimq5" /> : renderNoRole();
      case 4:
        return role ? <QuestionPicker role={role} selected={selected} onToggle={toggleItem} data-id="element-966" data-mp="2ix5as1mgmizp" /> : renderNoRole();
      case 5:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-967" data-mp="2mztmch2k640d">
            <div data-id="element-968" data-mp="2rxlar13zv7zt">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-969" data-mp="2ruz2ez9a4ogj">
                Rating Scale
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-970" data-mp="xj0vx52cu6oc">
                Define how respondents will rate each statement.
              </p>
            </div>
            <div className="space-y-4" data-id="element-971" data-mp="12iniw67zj1ac">
              <div data-id="element-972" data-mp="34fny0nvp8zpk">
                <label htmlFor="assessment-scale" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-973" data-mp="34f5s0zj8j74g">
                  Scale Type
                </label>
                <select id="assessment-scale" value={formData.scale} onChange={e => setFormData({
                ...formData,
                scale: e.target.value
              })} className="w-full md:w-1/2 px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm bg-white" data-id="element-974" data-mp="3e4flojtq8xuo">
                  <option value="5" data-id="element-975" data-mp="2vcmu8tub3kp2">5-Point Scale</option>
                  <option value="7" data-id="element-976" data-mp="2qf3fztxlz7x4">7-Point Scale</option>
                  <option value="10" data-id="element-977" data-mp="aoiaq8nqrlva">10-Point Scale</option>
                </select>
              </div>
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-978" data-mp="5pxk90yakgk1">
                <h4 className="text-sm font-medium text-slate-900 mb-3" data-id="element-979" data-mp="5s0c7qj08jre">
                  Preview ({formData.scale}-Point)
                </h4>
                <div className="flex justify-between items-start text-sm gap-1" data-id="element-980" data-mp="i9azlqa2xrl2">
                  {Array.from({
                  length: Number(formData.scale)
                }, (_, i) => {
                  const n = i + 1;
                  const last = Number(formData.scale);
                  return <div key={n} className="text-center flex-1" data-id="element-981" data-mp="vzeh63y0kx46">
                        <div className="w-8 h-8 rounded-full bg-white border border-slate-300 flex items-center justify-center mb-1 font-medium mx-auto" data-id="element-982" data-mp="10yrfply8zsml">
                          {n}
                        </div>
                        {(n === 1 || n === last) && <span className="text-xs text-slate-500" data-id="element-983" data-mp="r74wb2bran8r">
                            {n === 1 ? 'Strongly Disagree' : 'Strongly Agree'}
                          </span>}
                      </div>;
                })}
                </div>
              </div>
            </div>
          </div>;
      case 6:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-984" data-mp="2wr1ou3ffcfu5">
            <div data-id="element-985" data-mp="2hvlqvnbq3zsh">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-986" data-mp="2hu29tui8tkpn">
                Invite Respondents
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-987" data-mp="2msc31zkeqb8m">
                Select the subjects who will be evaluated in this cycle.
              </p>
            </div>
            <div className="relative" data-id="element-988" data-mp="2dtqr4n9fxugt">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" data-id="element-989" data-mp="2dvla3w15jci9" />
              <input type="text" aria-label="Search users or departments" placeholder="Search users or departments..." className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 sm:text-sm" data-id="element-990" data-mp="1z65n8r4qrr7u" />
            </div>
            <div className="border border-slate-200 rounded-lg overflow-hidden" data-id="element-991" data-mp="1bi8p2642ab6u">
              <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-992" data-mp="16iw4js0nxg7f">
                Suggested Users
              </div>
              <div className="divide-y divide-slate-100" data-id="element-993" data-mp="1fiav0rgtxqrx">
                {['Sarah Connor', 'Michael Scott', 'Jim Halpert'].map(name => <div key={name} className="flex items-center justify-between p-3 hover:bg-slate-50" data-id="element-994" data-mp="1ueid9m8ogi4k">
                    <div className="flex items-center" data-id="element-995" data-mp="238mynq51l1cp">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-600 mr-3" data-id="element-996" data-mp="23ahnhfjfpdm2">
                        {name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <span className="text-sm font-medium text-slate-900" data-id="element-997" data-mp="15ubtsv4ghfbx">
                        {name}
                      </span>
                    </div>
                    <button className="text-sm text-slate-600 hover:text-slate-900 font-medium px-3 py-1 border border-slate-200 rounded hover:bg-white" data-id="element-998" data-mp="19vwznwikotcm">
                      Add
                    </button>
                  </div>)}
              </div>
            </div>
          </div>;
      case 7:
        return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-999" data-mp="1tjeao0r9n45d">
            <div data-id="element-1000" data-mp="1tlh7p4qu5xjl">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-1001" data-mp="1xlhk6judjyo0">
                Review Assessment
              </h2>
              <p className="text-sm text-slate-500 mt-1" data-id="element-1002" data-mp="22kkqs8q06cy3">
                Please review the details and questionnaire before launching.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6" data-id="element-1003" data-mp="86bh01hw4upg">
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-1004" data-mp="88e6mkf4sltn">
                {[['Name', formData.name || 'Untitled Assessment'], ['Due Date', formData.dueDate || 'Not set'], ['Type', formData.type === 'self' ? 'Self-Assessment' : `${formData.type}° Feedback`], ['Role', role?.name ?? 'Not selected'], ['Scale', `${formData.scale}-Point`], ['Questions', role ? `${selected.length} of ${total} selected` : '—']].map(([label, value]) => <div key={label} data-id="element-1005" data-mp="en37plhtrzet">
                    <dt className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-1006" data-mp="9ngsx7mcmb3w">
                      {label}
                    </dt>
                    <dd className="font-medium text-slate-900" data-id="element-1007" data-mp="5ktzj2a14i4l">{value}</dd>
                  </div>)}
              </dl>
            </div>

            {role && incompleteDomains.length > 0 && <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-id="element-1008" data-mp="2qbb96nwi9xmi">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" data-id="element-1009" data-mp="i23de8eofq26" />
                <div className="text-sm text-amber-800" data-id="element-1010" data-mp="muwhqv68gwdq">
                  {incompleteDomains.length}{' '}
                  {incompleteDomains.length === 1 ? 'domain is' : 'domains are'}{' '}
                  incomplete: {incompleteDomains.map(d => d.name).join(', ')}.{' '}
                  <button type="button" onClick={() => setCurrentStep(4)} className="font-medium underline underline-offset-2" data-id="element-1011" data-mp="10tmg0e4sh23o">
                    Finish selecting questions
                  </button>
                </div>
              </div>}

            {role ? <div data-id="element-1012" data-mp="vwno828ll78n">
                <h3 className="text-sm font-semibold text-slate-900 mb-3" data-id="element-1013" data-mp="3cjot248upg2s">
                  Questionnaire preview
                </h3>
                <QuestionnairePreview role={role} selected={selected} data-id="element-1014" data-mp="3gltgumcmbihi" />
              </div> : renderNoRole()}
          </div>;
      case 8:
        return <div className="py-12 text-center animate-in fade-in zoom-in duration-500" data-id="element-1015" data-mp="341wh89npj6za">
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6" data-id="element-1016" data-mp="3iydu55bjotny">
              <Rocket className="h-8 w-8 text-emerald-600" data-id="element-1017" data-mp="3e15cd5ub6rn8" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-1018" data-mp="2qc2t5tk10qqm">
              Assessment Launched!
            </h2>
            <p className="text-slate-500 max-w-md mx-auto mb-8" data-id="element-1019" data-mp="2ldhptl9h9qxy">
              Your {role ? `${role.name} ` : ''}assessment with {selected.length}{' '}
              questions has been created and invitations have been sent to the
              selected respondents.
            </p>
            <div className="flex justify-center space-x-4" data-id="element-1020" data-mp="27j8obsg4af04">
              <Link to="/app/assessments" className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-1021" data-mp="22loxzu65abrt">
                View Assessments
              </Link>
              <Link to="/app" className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors" data-id="element-1022" data-mp="2w936vvfevo0m">
                Go to Dashboard
              </Link>
            </div>
          </div>;
      default:
        return null;
    }
  };
  const pendingRole = pendingRoleId ? getRole(pendingRoleId) : undefined;
  return <div className="max-w-6xl mx-auto animate-in fade-in duration-500" data-id="element-1023" data-mp="2ibemspt64kwm">
      <div className="mb-8" data-id="element-1024" data-mp="2ia3kyvja9n85">
        <Link to="/app/assessments" className="text-sm font-medium text-slate-500 hover:text-slate-900 flex items-center mb-4" data-id="element-1025" data-mp="2nahuki0j958a">
          <ChevronLeft className="h-4 w-4 mr-1" data-id="element-1026" data-mp="3tq1wr75uind2" /> Back to Assessments
        </Link>
        <h1 className="text-2xl font-semibold text-slate-900" data-id="element-1027" data-mp="2j7ha4ae9gcg">
          Create New Assessment
        </h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden flex flex-col md:flex-row min-h-[600px]" data-id="element-1028" data-mp="1n1jh22k0sqde">
        {/* Sidebar Progress */}
        <div className="w-full md:w-60 bg-slate-50 border-b md:border-b-0 md:border-r border-slate-200 p-6 shrink-0 hidden md:block" data-id="element-1029" data-mp="1dbq9dcn00vef">
          <div className="space-y-2" data-id="element-1030" data-mp="18d55n38jftl8">
            {STEPS.map(step => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isPast = currentStep > step.id;
            return <div key={step.id} className={`flex items-center p-2 rounded-lg transition-colors ${isActive ? 'bg-white shadow-sm border border-slate-200' : 'border border-transparent'}`} data-id="element-1031" data-mp="ud43zlkg8i4p">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 shrink-0 ${isActive ? 'bg-slate-900 text-white' : isPast ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-200 text-slate-400'}`} data-id="element-1032" data-mp="2or52u6g0154s">
                    {isPast ? <CheckCircle2 className="h-5 w-5" data-id="element-1033" data-mp="1xlt02bm5miby" /> : <Icon className="h-4 w-4" data-id="element-1034" data-mp="1jtuu29nqp2k0" />}
                  </div>
                  <span className={`text-sm font-medium ${isActive ? 'text-slate-900' : isPast ? 'text-slate-700' : 'text-slate-400'}`} data-id="element-1035" data-mp="1nvg5rrojfasm">
                    {step.title}
                  </span>
                </div>;
          })}
          </div>
          {role && <div className="mt-6 pt-6 border-t border-slate-200" data-id="element-1036" data-mp="10xmqdaadkzqt">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-1" data-id="element-1037" data-mp="14wvikapropk9">
                Role
              </div>
              <div className="text-sm font-medium text-slate-900" data-id="element-1038" data-mp="2vakwdhp1la8c">
                {role.name}
              </div>
              <div className="text-xs text-slate-500 mt-1" data-id="element-1039" data-mp="2zavbbjutbazc">
                {selected.length} of {total} questions
              </div>
            </div>}
        </div>

        {/* Mobile Progress Bar */}
        <div className="md:hidden p-4 border-b border-slate-200 bg-slate-50" data-id="element-1040" data-mp="jkwku6e9f9ux">
          <div className="flex items-center justify-between text-sm font-medium text-slate-700 mb-2" data-id="element-1041" data-mp="xffg7xvsdd3q">
            <span data-id="element-1042" data-mp="sm36etvrnav1">
              Step {currentStep} of {STEPS.length}
            </span>
            <span data-id="element-1043" data-mp="17aqy2p2hr0fp">{Math.round(currentStep / STEPS.length * 100)}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2" data-id="element-1044" data-mp="12cfo8ec74pxh">
            <div className="bg-slate-900 h-2 rounded-full transition-all duration-300" style={{
            width: `${currentStep / STEPS.length * 100}%`
          }} data-id="element-1045" data-mp="1gdib2tb0m49g" />
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0" data-id="element-1046" data-mp="3i4llpikwniu0">
          <div className="flex-1 p-6 md:p-8 overflow-y-auto" data-id="element-1047" data-mp="3d61zdh2eg514">
            {renderStepContent()}
          </div>

          {currentStep < LAST_STEP && <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-between items-center gap-4 mt-auto" data-id="element-1048" data-mp="2leoxjbx35wdi">
              <button onClick={handleBack} disabled={currentStep === 1} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-1049" data-mp="2u93km0nuyb76">
                Back
              </button>
              <div className="flex items-center gap-3" data-id="element-1050" data-mp="1xkf79hn37c43">
                {currentStep === 4 && role && !selectionComplete && <span className="hidden sm:inline text-xs text-slate-500" data-id="element-1051" data-mp="2bg97lc9sot5l">
                    {incompleteDomains.length} domain
                    {incompleteDomains.length === 1 ? '' : 's'} left
                  </span>}
                <button onClick={handleNext} disabled={!canContinue} className="px-6 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800 transition-colors flex items-center disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-1052" data-mp="3hzh5n780z9s9">
                  {currentStep === REVIEW_STEP ? 'Launch Assessment' : 'Continue'}
                  {currentStep !== REVIEW_STEP && <ChevronRight className="h-4 w-4 ml-1" data-id="element-1053" data-mp="3lig91nsqoi2g" />}
                </button>
              </div>
            </div>}
        </div>
      </div>

      {/* Role change confirmation */}
      {pendingRole && <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-1054" data-mp="3bs4bz1lpwxb">
          <div className="absolute inset-0 bg-slate-900/30" onClick={() => setPendingRoleId(null)} data-id="element-1055" data-mp="89jxewdzfp33" />
          <div role="alertdialog" aria-modal="true" aria-labelledby="role-change-title" className="relative w-full max-w-md bg-white rounded-xl shadow-xl p-6" data-id="element-1056" data-mp="vyw7rzl1q36z">
            <div className="flex items-start gap-3" data-id="element-1057" data-mp="1allbyeq8t4jp">
              <div className="w-9 h-9 rounded-full bg-amber-50 flex items-center justify-center shrink-0" data-id="element-1058" data-mp="15n0euo6af0zh">
                <AlertTriangle className="h-5 w-5 text-amber-600" data-id="element-1059" data-mp="10n5mshas05cu" />
              </div>
              <div data-id="element-1060" data-mp="wnd9xsecnd1o">
                <h2 id="role-change-title" className="text-base font-semibold text-slate-900" data-id="element-1061" data-mp="wlts3xd1otxs">
                  Change role to {pendingRole.name}?
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-1062" data-mp="mvhc8ig6doxz">
                  Each role has different competency domains, so your{' '}
                  {selected.length} selected{' '}
                  {selected.length === 1 ? 'question' : 'questions'} will be
                  cleared.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6" data-id="element-1063" data-mp="3xegzlx1avrq">
              <button onClick={() => setPendingRoleId(null)} className="px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-1064" data-mp="400yoedn2rno">
                Keep current role
              </button>
              <button onClick={confirmRoleChange} className="px-4 py-2 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800" data-id="element-1065" data-mp="2on9aavajsia4">
                Change role
              </button>
            </div>
          </div>
        </div>}
    </div>;
}